import React, { createContext, useContext, useState, useEffect, useRef, ReactNode, useCallback } from 'react';
import { MenuVisibility as MenuVisibilityType, platformSettingsAPI, barrattAPI, type FeatureFlags, type DashboardTabsVisibility, type SupplierCardFieldsVisibility, type DataImportTilesVisibility, type SupplierProfileLayout, type HelpLayout, type OpenOrdersVisibility, type DeliveryGrain, type PurchasingTabsVisibility, type QualityTabsVisibility } from '../lib/api';

import { applyHierarchyLabels } from '../components/orgchart/types';
import { useOptionalAuth } from './AuthContext';

/** Fired by ViewContext whenever the active site / company / group scope changes. */
export const VIEW_SCOPE_CHANGED_EVENT = 'helix:view-scope-changed';

// Re-export MenuVisibility for convenience
export type MenuVisibility = MenuVisibilityType;

// Portal type values: '1' = Customer, '2' = Supplier
export type MainMenuType = '1' | '2';

// Portal mode: 'standard' = Helix default, 'spend_analytics' = Spend Analytics
// module layout, 'compliance' = Compliance module, 'customer_portal' = Supplier
// is platform owner.
// CR-5334: 'barratt' was renamed to the neutral 'spend_analytics' (no section
// should reference a specific client by name). 'barratt' is retained in the
// union only so the FE still reads any not-yet-migrated stored value.
export type PortalMode = 'standard' | 'spend_analytics' | 'compliance' | 'customer_portal' | 'barratt';

interface MenuSettingsContextType {
  menuVisibility: MenuVisibility;
  updateMenuVisibility: (settings: MenuVisibility) => void;
  isMenuItemVisible: (key: keyof MenuVisibility) => boolean;
  loadSettingsFromBackend: () => Promise<void>;
  isLoading: boolean;
  // Platform-wide portal type setting (controls default portal experience)
  mainMenuType: MainMenuType;
  // Helper to check if the platform default is customer portal
  isCustomerPortalDefault: boolean;
  // Helper to check if the platform default is supplier portal
  isSupplierPortalDefault: boolean;
  // CR-6385: concession gating. Resolved server-side per role, NOT read from
  // featureFlags — those merge from the viewer's own company, and the viewer
  // is usually the supplier while the tenant that opted in is the customer.
  /** True when at least one customer this user works with has concessions on. */
  concessionsEnabled: boolean;
  /** Customer ids with concessions on — check a PO line's customer against this. */
  concessionEnabledCustomerIds: number[];
  /** Is the Raise Concession entry point allowed for this line's customer? */
  isConcessionEnabledForCustomer: (customerId?: number | null) => boolean;
  /** CR-7353: at least one customer this user works with has the NCR Escape root-cause chain on. */
  escapeRootCauseEnabled: boolean;
  /** CR-7353: is the Escape root-cause chain on for this NCR's customer? */
  isEscapeRootCauseEnabledForCustomer: (customerId?: number | null) => boolean;
  // Barratt feature flags
  featureFlags: FeatureFlags;
  /**
   * Boolean feature-flag check. `po_line_schedules` resolves to the EFFECTIVE
   * per-customer delivery grain for the active scope (see `deliveryGrain`),
   * not the raw platform flag, so every gate that asks this question follows
   * the customer being viewed.
   */
  isFeatureEnabled: (feature: Exclude<keyof FeatureFlags, 'portal_mode' | 'dashboard_tabs' | 'supplier_dashboard_tabs'>) => boolean;
  /**
   * Per-customer delivery grain for the ACTIVE scope (site / company
   * selection, or the single customer a supplier trades with): 'schedules'
   * (POLineSchedule rows) or 'po_lines'. Served by the display-settings
   * endpoint as `delivery_grain`; falls back to the platform
   * `po_line_schedules` flag on an older backend.
   */
  deliveryGrain: DeliveryGrain;
  /** Convenience: `deliveryGrain === 'schedules'`. */
  usesScheduleLines: boolean;
  /** The customer the effective grain was resolved for (null = platform). */
  deliveryGrainCustomerId: number | null;
  // Portal mode - determines navigation structure (standard Helix vs Spend Analytics)
  portalMode: PortalMode;
  // CR-5334: renamed from isBarrattMode. isBarrattMode kept as a deprecated
  // alias (same value) so any straggling consumer keeps working.
  isSpendAnalyticsMode: boolean;
  /** @deprecated CR-5334 — use isSpendAnalyticsMode. */
  isBarrattMode: boolean;
  isComplianceMode: boolean;
  isCustomerPortalMode: boolean;
  // Dashboard tab visibility (CR-4441)
  dashboardTabs: DashboardTabsVisibility;
  supplierDashboardTabs: DashboardTabsVisibility;
  isDashboardTabEnabled: (tabId: string, isSupplier?: boolean) => boolean;
  updateDashboardTabs: (tabs: DashboardTabsVisibility) => void;
  updateSupplierDashboardTabs: (tabs: DashboardTabsVisibility) => void;
  // Supplier card field visibility
  supplierCardFields: SupplierCardFieldsVisibility;
  isSupplierCardFieldEnabled: (fieldId: keyof SupplierCardFieldsVisibility) => boolean;
  updateSupplierCardFields: (fields: SupplierCardFieldsVisibility) => void;
  // Data Import tile visibility
  dataImportTiles: DataImportTilesVisibility;
  isDataImportTileEnabled: (tileId: keyof DataImportTilesVisibility) => boolean;
  updateDataImportTiles: (tiles: DataImportTilesVisibility) => void;
  // Default landing path for approved supplier users (pre-approval suppliers are
  // always routed to /onboarding/my-progress).
  supplierDefaultLandingPath: string;
  updateSupplierDefaultLandingPath: (path: string) => void;
  supplierProfileLayout: SupplierProfileLayout;
  updateSupplierProfileLayout: (layout: SupplierProfileLayout) => void;
  helpLayout: HelpLayout;
  updateHelpLayout: (layout: HelpLayout) => void;
  // Quality measure toggle: false = PPM (default), true = PPK.
  // Drives defect-rate labels across dashboards, settings, drill-downs.
  qualityMeasurePpk: boolean;
  // Master switch for the 8D/A3 scoring feature (RC/CA/8D score columns + the
  // customer's RC/CA grading input on the CAR). Defaults true (feature on).
  enable8dScoring: boolean;
  /** ERP writeback master switch. Absent (older payload) => on. */
  writebackEnabled: boolean;
  /** CR-6312: warehouse module toggle — gates warehouse UI (e.g. the
   *  Supplier Performance warehouse filter). Absent => off. */
  warehouseModuleEnabled: boolean;
  // Bulk delivery appeals, BOTH sides — supplier multi-select "delivered on
  // time" AND customer multi-select accept/reject. Off by default; the per-row
  // single appeal and single respond are always available regardless.
  deliveryAppealBulkEnabled: boolean;
  /** Audit Lite: Audits tabs on Questionnaires and the supplier profile (customer side). Off by default. */
  auditLiteEnabled: boolean;
  // Notification bell placement (both may be true — "keep both").
  notificationIconTop: boolean;
  notificationIconSide: boolean;
  // NCR: Fault Group/Category optional (not mandatory) on the create form.
  ncrFaultClassificationOptional: boolean;
  // CR-5884: Open Orders supplier-facing hide-toggles. Each key, when true,
  // hides the corresponding control in the supplier's Open Orders / PO Lines
  // / Deliveries / Forecast / detail-modal views. Customer UI is unaffected —
  // call sites AND the flag with `isSupplier` (CR-8701: not hide_communication_box).
  openOrdersVisibility: OpenOrdersVisibility;
  /** E4: what this customer calls its own NCR number (the create form's label). */
  ncrCustomerReferenceLabel: string;
  /**
   * Returns true when an Open Orders control should be hidden for the
   * current viewer. Pass `isSupplier` from the caller (typically derived
   * from `useAuth().user?.platform === 'SUPPLIER'` or `usePortalMode().isSupplier`).
   * Customer / Helix viewers see it regardless (CR-8701: not hide_communication_box).
   */
  isOpenOrdersControlHidden: (key: keyof OpenOrdersVisibility, isSupplier: boolean) => boolean;
  /**
   * CR-7058: the sub-toggles nested under Purchasing and Quality in
   * Platform Admin → Navigation Menu Management. They gate tabs, but three of
   * them also own whole routes (/rfq, /concessions, /quality/approval-register),
   * so NavigationAccessGate needs them alongside the top-level menu keys.
   * Pages that render the tab strips may keep their own fetch; this is the
   * copy the route gate reads.
   */
  purchasingTabs: PurchasingTabsVisibility;
  qualityTabs: QualityTabsVisibility;
}

const MenuSettingsContext = createContext<MenuSettingsContextType | undefined>(undefined);

export const DEFAULT_MENU_VISIBILITY: MenuVisibility = {
  // Customer Menu Items - All enabled by default
  dashboard: true,
  supplierPerformance: true,
  calendar: true,
  purchasing: true,
  rfq: true,
  quality: true,
  concessions: true,
  suppliers: true,
  documents: true,
  supplierIntelligence: true,
  supplierOnboarding: true,
  userInvitations: true,
  userActivityHistory: true,
  questionnaire: true,
  newsRelease: true,
  adminSetup: true,
  partsMaster: true,
  partSchedule: false,  // Off by default — opt-in per install
  analytics: true,  // Default true so existing installs don't silently lose the Analytics sidebar item
  dataImport: true,
  // Supplier Menu Items - All enabled by default
  purchaseOrders: true,
  companyProfile: true,
  customers: true,  // Customers list for supplier portal
  callOff: true,     // Call Off tab in Purchasing
  supplierDashboard: true,
  supplierQuestionnaires: true,
  supplierCalendar: true,
  supplierCustomers: true,
  supplierDocuments: true,
  supplierNewsRelease: true,
  supplierSelfPerformance: true,  // SI-16
  supplierRfq: false,  // Off by default — switched on per install that runs supplier RFQs
};

const DEFAULT_DATA_IMPORT_TILES: DataImportTilesVisibility = {
  commodity: true,
  faultcodes: true,
  commonised_ca: true,
  departments: true,
  warehouses: true,
  suppliers: true,
  supplier_additional: true,
  grns: true,
  pos: true,
  ncrs: true,
  forecasts: true,
  users: true,
};

/** CR-7058: permissive defaults, matching the per-page defaults they replace. */
export const DEFAULT_PURCHASING_TABS: PurchasingTabsVisibility = {
  rfq: true, pricing: true, po_headers: true, po_lines: true,
  forecast: true, asn: true, deliveries: true, call_off: true,
  late_to_raise: false,
};

export const DEFAULT_QUALITY_TABS: QualityTabsVisibility = {
  ncrs: true,
  concessions: true,
  approval_register: true,
};

const DEFAULT_DASHBOARD_TABS: DashboardTabsVisibility = {
  'default': true,
  'scheduled': false,
  'spot-orders': false,
  'po-lines': true,
  'group-kpi': true,
  'group-detail-kpi': true,
  'site-kpi': true,
  'cost': true,
  'emissions': true,
  'compliance': true,
  'future-forecast': true,
  'date-adherence': true,
  'expediting': true,
  'performance-ratings': true,
};

const DEFAULT_SUPPLIER_DASHBOARD_TABS: DashboardTabsVisibility = {
  'default': true,
  'scheduled': false,
  'spot-orders': false,
  'po-lines': false,
  'group-kpi': false,
  'group-detail-kpi': false,
  // CR-5868: Site KPI (Delivery + Quality breakdown, per customer site) is on
  // for suppliers by default — the backend already scopes it to the supplier's
  // own orders. A tenant's saved supplier_dashboard_tabs still overrides this.
  // Keep in step with admin/settings/types.ts DEFAULT_SUPPLIER_DASHBOARD_TABS.
  'site-kpi': true,
  'cost': true,
  'emissions': false,
  'compliance': true,
  'future-forecast': false,
  'date-adherence': true,
  'expediting': false,
  'performance-ratings': true,
  // CR-6310 tab was never added to the supplier map, and the fallback in
  // isDashboardTabEnabled hides anything absent from it when isSupplier —
  // so suppliers could never see their own scorecard.
  'performance': true,
};

const DEFAULT_SUPPLIER_CARD_FIELDS: SupplierCardFieldsVisibility = {
  logo: true,
  risk_badge: true,
  otd: true,
  rating: true,
  questionnaire_status: true,
  otif_quick_filter: true,
  otif: true,
};

const DEFAULT_OPEN_ORDERS_VISIBILITY: OpenOrdersVisibility = {
  hide_exceptions: false,
  hide_decline_button: false,
  hide_propose_qty_ordered: false,
  hide_actions: false,
  hide_trend_line: false,
  hide_otif_override_button: false,
  hide_forecasting_from_supplier: false,
  hide_communication_box: false,
};

const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  delivery_appeals: false,
  spend_leakage: false,
  bulk_upload: false,
  commodity_codes: false,
  supplier_stock_levels: false,
  switch_call_off_tab: false,
  logistics_view: false,
  allow_supplier_decline: false,
  po_line_schedules: false,
  schedule_collaboration: false,
  show_forecast_total: true,
  forecast_weeks_only: false,
  forecast_hide_supplier_part_no: false,
  forecast_description_from_master: false,
  asn_produmax_export: false,
  asn_logistics_tracking: false,
  // CR-6274: default ON. Every install that predates the toggle already has
  // GRN Repair, so the switch must start enabled and only hide the module
  // when an admin deliberately turns it off.
  grn_repair: true,
  portal_mode: 'standard',
};

/**
 * Provides platform-wide menu visibility, feature flags, and portal mode settings.
 * Loads feature flags for all authenticated users and full platform settings for admins only.
 */
export const MenuSettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Start with defaults - will be replaced by backend data
  const [menuVisibility, setMenuVisibility] = useState<MenuVisibility>(DEFAULT_MENU_VISIBILITY);
  const [purchasingTabs, setPurchasingTabs] = useState<PurchasingTabsVisibility>(DEFAULT_PURCHASING_TABS);
  const [qualityTabs, setQualityTabs] = useState<QualityTabsVisibility>(DEFAULT_QUALITY_TABS);
  const [isLoading, setIsLoading] = useState(true);
  // Platform-wide main menu type: '1' = Customer Portal, '2' = Supplier Portal (default)
  const [mainMenuType, setMainMenuType] = useState<MainMenuType>('2');
  // Barratt feature flags
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>(DEFAULT_FEATURE_FLAGS);
  // Per-customer delivery grain for the active scope. null until the
  // display-settings payload says otherwise (older backends never will), in
  // which case the platform flag is the answer.
  const [effectivePoLineSchedules, setEffectivePoLineSchedules] = useState<boolean | null>(null);
  const [deliveryGrainCustomerId, setDeliveryGrainCustomerId] = useState<number | null>(null);
  // CR-6385: default off until the backend says otherwise, so a slow or failed
  // settings load hides the feature rather than briefly offering it.
  const [concessionsEnabled, setConcessionsEnabled] = useState(false);
  const [concessionEnabledCustomerIds, setConcessionEnabledCustomerIds] = useState<number[]>([]);
  // CR-7353: NCR Escape root-cause chain, off until the backend says otherwise.
  const [escapeRootCauseCustomerIds, setEscapeRootCauseCustomerIds] = useState<number[]>([]);
  // Dashboard tab visibility (CR-4441)
  const [dashboardTabs, setDashboardTabs] = useState<DashboardTabsVisibility>(DEFAULT_DASHBOARD_TABS);
  const [supplierDashboardTabs, setSupplierDashboardTabs] = useState<DashboardTabsVisibility>(DEFAULT_SUPPLIER_DASHBOARD_TABS);
  // Supplier card field visibility
  const [supplierCardFields, setSupplierCardFields] = useState<SupplierCardFieldsVisibility>(DEFAULT_SUPPLIER_CARD_FIELDS);
  // Data Import tile visibility
  const [dataImportTiles, setDataImportTiles] = useState<DataImportTilesVisibility>(DEFAULT_DATA_IMPORT_TILES);
  // Supplier default landing path
  const [supplierDefaultLandingPath, setSupplierDefaultLandingPath] = useState<string>('/dashboard');
  // Supplier Profile page layout (which stats/tabs/widgets to show)
  const [supplierProfileLayout, setSupplierProfileLayout] = useState<SupplierProfileLayout>({
    stats: { otd: true, total_orders: true },
    tabs: {
      performance: true, orders: true, contracts: true, quality: true,
      questionnaires: true, credit_scores: true, users: true,
      activity_log: true, group_structure: true, approvals: true, calendar: true,
    },
    overview: { performance_summary: true, capabilities: true },
  });
  // Quality measure toggle: false = PPM (default, legacy), true = PPK.
  const [qualityMeasurePpk, setQualityMeasurePpk] = useState<boolean>(false);
  // Defaults true so the scoring feature stays visible on tenants whose
  // settings payload predates this flag (matches the backend default).
  const [enable8dScoring, setEnable8dScoring] = useState<boolean>(true);
  const [writebackEnabled, setWritebackEnabled] = useState<boolean>(true);
  const [warehouseModuleEnabled, setWarehouseModuleEnabled] = useState<boolean>(false);
  // Bulk delivery appeals, BOTH sides — supplier bulk raise AND customer bulk
  // accept/reject share this one flag (off by default) + notification bell
  // placement (top on, side off by default — matches the backend defaults).
  const [deliveryAppealBulkEnabled, setDeliveryAppealBulkEnabled] = useState<boolean>(false);
  const [auditLiteEnabled, setAuditLiteEnabled] = useState<boolean>(false);
  const [notificationIconTop, setNotificationIconTop] = useState<boolean>(true);
  const [notificationIconSide, setNotificationIconSide] = useState<boolean>(false);
  const [ncrFaultClassificationOptional, setNcrFaultClassificationOptional] = useState<boolean>(false);
  // CR-5884: Open Orders hide-toggle map (all default OFF -> nothing hidden).
  const [openOrdersVisibility, setOpenOrdersVisibility] = useState<OpenOrdersVisibility>(DEFAULT_OPEN_ORDERS_VISIBILITY);
  const [ncrCustomerReferenceLabel, setNcrCustomerReferenceLabel] = useState<string>('Customer NCR No.');
  // Help & Support page layout — which FAQ categories and sidebar cards
  // a customer wants visible. All on by default; superuser toggles from admin.
  const [helpLayout, setHelpLayout] = useState<HelpLayout>({
    faq: {
      getting_started: true,
      onboarding: true,
      questionnaires: true,
      purchasing: true,
      supplier_management: true,
      documents_communication: true,
    },
    sidebar: { tour: true, contact_support: true, system_status: true },
  });

  // Function to load settings from backend
  const loadSettingsFromBackend = useCallback(async () => {
    // Only fetch if user is authenticated (token exists)
    // Check localStorage and sessionStorage for token (key may be in either)
    const token = localStorage.getItem('helix_auth_token') || sessionStorage.getItem('helix_auth_token') || localStorage.getItem('token');
    if (!token) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    // First, load feature flags for ALL authenticated users
    try {
      const flagsResponse = await barrattAPI.getFeatureFlags();
      if (flagsResponse.data) {
        setFeatureFlags({
          ...DEFAULT_FEATURE_FLAGS,
          ...flagsResponse.data,
        });
        // CR-4441: Load dashboard tab visibility from feature flags (available to all users)
        if (flagsResponse.data.dashboard_tabs && typeof flagsResponse.data.dashboard_tabs === 'object') {
          setDashboardTabs({
            ...DEFAULT_DASHBOARD_TABS,
            ...flagsResponse.data.dashboard_tabs,
          });
        }
        if (flagsResponse.data.supplier_dashboard_tabs && typeof flagsResponse.data.supplier_dashboard_tabs === 'object') {
          setSupplierDashboardTabs({
            ...DEFAULT_SUPPLIER_DASHBOARD_TABS,
            ...flagsResponse.data.supplier_dashboard_tabs,
          });
        }
      }
    } catch {
      // Feature flags endpoint might not exist or be accessible
      // Keep using defaults
    }

    // Load platform display settings for ALL authenticated users
    // These are platform-wide settings (tab visibility, feature flags, menu config)
    try {
      const response = await platformSettingsAPI.getDisplaySettings();
      if (response.data) {
        // Load menu visibility settings
        if (response.data.menu_visibility) {
          setMenuVisibility({
            ...DEFAULT_MENU_VISIBILITY,
            ...response.data.menu_visibility,
          });
        }
        // Load main_menu_type (portal type setting)
        if (response.data.main_menu_type) {
          const menuType = response.data.main_menu_type;
          if (menuType === '1' || menuType === '2') {
            setMainMenuType(menuType);
          }
        }
        // Load feature flags
        if (response.data.feature_flags) {
          setFeatureFlags({
            ...DEFAULT_FEATURE_FLAGS,
            ...response.data.feature_flags,
          });
        }
        // Per-customer delivery grain for the active scope (top-level, not
        // inside feature_flags — those keep the platform value).
        const eff = (response.data as Record<string, unknown>)['effective_po_line_schedules'];
        setEffectivePoLineSchedules(typeof eff === 'boolean' ? eff : null);
        const grainCid = (response.data as Record<string, unknown>)['delivery_grain_customer_id'];
        setDeliveryGrainCustomerId(typeof grainCid === 'number' ? grainCid : null);
        // CR-6385: concession gating (top-level, not inside feature_flags).
        setConcessionsEnabled(!!response.data.concessions_enabled);
        setConcessionEnabledCustomerIds(response.data.concession_enabled_customer_ids ?? []);
        // CR-7353: Escape root-cause chain gating (top-level, resolved per role).
        setEscapeRootCauseCustomerIds(response.data.ncr_escape_root_cause_customer_ids ?? []);
        // CR-7058: Purchasing / Quality sub-toggles, so the route gate can
        // refuse the pages they own (/rfq, /concessions, approval register).
        if (response.data.purchasing_tabs) {
          setPurchasingTabs({
            ...DEFAULT_PURCHASING_TABS,
            ...response.data.purchasing_tabs,
          });
        }
        if (response.data.quality_tabs) {
          setQualityTabs({
            ...DEFAULT_QUALITY_TABS,
            ...response.data.quality_tabs,
          });
        }
        // Load dashboard tab visibility (CR-4441)
        if (response.data.dashboard_tabs) {
          setDashboardTabs({
            ...DEFAULT_DASHBOARD_TABS,
            ...response.data.dashboard_tabs,
          });
        }
        if (response.data.supplier_dashboard_tabs) {
          setSupplierDashboardTabs({
            ...DEFAULT_SUPPLIER_DASHBOARD_TABS,
            ...response.data.supplier_dashboard_tabs,
          });
        }
        // Load supplier card field visibility
        const cardFields = (response.data as Record<string, unknown>)['supplier_card_fields'] as Record<string, boolean> | undefined;
        if (cardFields) {
          setSupplierCardFields({
            ...DEFAULT_SUPPLIER_CARD_FIELDS,
            ...cardFields,
          });
        }
        // Load Data Import tile visibility
        const importTiles = (response.data as Record<string, unknown>)['data_import_tiles'] as Record<string, boolean> | undefined;
        if (importTiles) {
          setDataImportTiles({
            ...DEFAULT_DATA_IMPORT_TILES,
            ...importTiles,
          });
        }
        // Load supplier default landing path
        const landing = (response.data as Record<string, unknown>)['supplier_default_landing_path'] as string | undefined;
        if (landing) {
          setSupplierDefaultLandingPath(landing);
        }
        // Load supplier profile layout
        const layout = (response.data as Record<string, unknown>)['supplier_profile_layout'] as SupplierProfileLayout | undefined;
        if (layout && typeof layout === 'object' && layout.tabs) {
          setSupplierProfileLayout(prev => ({
            stats: { ...prev.stats, ...(layout.stats || {}) },
            tabs: { ...prev.tabs, ...(layout.tabs || {}) },
            overview: { ...prev.overview, ...(layout.overview || {}) },
          }));
        }
        // Load quality measure toggle (PPM vs PPK). Falls back to false (PPM).
        const qmp = (response.data as Record<string, unknown>)['quality_measure_ppk'];
        setQualityMeasurePpk(typeof qmp === 'boolean' ? qmp : false);
        // Load 8D/A3 scoring master switch. Absent (older payload) => on.
        const e8d = (response.data as Record<string, unknown>)['enable_8d_scoring'];
        setEnable8dScoring(typeof e8d === 'boolean' ? e8d : true);
        // ERP writeback master switch. Absent (older payload) => on, so an
        // older backend keeps the tab rather than hiding a working feature.
        const wbe = (response.data as Record<string, unknown>)['writeback_enabled'];
        setWritebackEnabled(typeof wbe === 'boolean' ? wbe : true);
        // Warehouse module toggle. Absent (older payload) => off, matching
        // the model default.
        const whm = (response.data as Record<string, unknown>)['warehouse_module'];
        setWarehouseModuleEnabled(typeof whm === 'boolean' ? whm : false);
        // Bulk delivery appeals (both sides) + notification bell placement.
        // Absent => defaults.
        const dab = (response.data as Record<string, unknown>)['delivery_appeal_bulk_enabled'];
        setDeliveryAppealBulkEnabled(typeof dab === 'boolean' ? dab : false);
        const alt = (response.data as Record<string, unknown>)['audit_lite_enabled'];
        setAuditLiteEnabled(typeof alt === 'boolean' ? alt : false);
        const nit = (response.data as Record<string, unknown>)['notification_icon_top'];
        setNotificationIconTop(typeof nit === 'boolean' ? nit : true);
        const nis = (response.data as Record<string, unknown>)['notification_icon_side'];
        setNotificationIconSide(typeof nis === 'boolean' ? nis : false);
        const nfco = (response.data as Record<string, unknown>)['ncr_fault_classification_optional'];
        setNcrFaultClassificationOptional(typeof nfco === 'boolean' ? nfco : false);
        // CR-5884: Load Open Orders supplier-facing hide-toggles. Merge over
        // defaults so newly-added flags stay OFF on tenants that haven't
        // saved a value yet.
        const refLabel = (response.data as Record<string, unknown>)['ncr_customer_reference_label'];
        if (typeof refLabel === 'string' && refLabel.trim()) setNcrCustomerReferenceLabel(refLabel);
        const ooVis = (response.data as Record<string, unknown>)['open_orders_visibility'] as Partial<OpenOrdersVisibility> | undefined;
        if (ooVis && typeof ooVis === 'object') {
          const merged: OpenOrdersVisibility = { ...DEFAULT_OPEN_ORDERS_VISIBILITY };
          (Object.keys(DEFAULT_OPEN_ORDERS_VISIBILITY) as Array<keyof OpenOrdersVisibility>).forEach((k) => {
            const v = ooVis[k];
            if (typeof v === 'boolean') merged[k] = v;
          });
          setOpenOrdersVisibility(merged);
        }
        // Load Help & Support page layout
        const help = (response.data as Record<string, unknown>)['help_layout'] as HelpLayout | undefined;
        if (help && typeof help === 'object') {
          setHelpLayout(prev => ({
            faq: { ...prev.faq, ...(help.faq || {}) },
            sidebar: { ...prev.sidebar, ...(help.sidebar || {}) },
          }));
        }
        // Apply configurable hierarchy labels platform-wide
        const labels = (response.data as Record<string, unknown>)['hierarchy_labels'] as { group?: string; division?: string; site?: string } | undefined;
        if (labels) {
          applyHierarchyLabels({
            group: labels.group || 'Group',
            division: labels.division || 'Division',
            site: labels.site || 'Company',
          });
        }
      }
    } catch (error) {
      console.error('Failed to load platform display settings:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load settings from backend on initial mount if authenticated
  useEffect(() => {
    loadSettingsFromBackend();
  }, [loadSettingsFromBackend]);

  // CR-6282 follow-up: a tab opened from a link (target="_blank") starts with
  // no token and receives the session from an open tab over BroadcastChannel
  // AFTER this provider mounted, so the mount-time load above found no token and
  // the tab kept the default menus — no GRN Repair, no Scheduled dashboard —
  // until something else reloaded settings. Load again once a token arrives.
  const authToken = useOptionalAuth()?.token ?? null;
  const hadTokenRef = useRef<boolean>(!!authToken);
  useEffect(() => {
    if (authToken && !hadTokenRef.current) loadSettingsFromBackend();
    hadTokenRef.current = !!authToken;
  }, [authToken, loadSettingsFromBackend]);

  // The effective delivery grain is per active customer scope, so a site /
  // company switch (ViewContext) must re-resolve it. ViewContext wraps this
  // provider, so it signals with a window event rather than a context hook.
  useEffect(() => {
    const onScopeChange = () => { loadSettingsFromBackend(); };
    window.addEventListener(VIEW_SCOPE_CHANGED_EVENT, onScopeChange);
    return () => window.removeEventListener(VIEW_SCOPE_CHANGED_EVENT, onScopeChange);
  }, [loadSettingsFromBackend]);

  const updateMenuVisibility = (settings: MenuVisibility) => {
    setMenuVisibility(settings);
  };

  const isMenuItemVisible = (key: keyof MenuVisibility): boolean => {
    // Fall back to DEFAULT_MENU_VISIBILITY rather than an unconditional `true`
    // so items that default to hidden (e.g. partSchedule) remain hidden when
    // the backend's stored menu_visibility doesn't explicitly include the key.
    // If the key is unknown to defaults, keep the legacy behaviour of
    // treating it as visible.
    return menuVisibility[key] ?? DEFAULT_MENU_VISIBILITY[key] ?? true;
  };

  // Derived helpers for portal type
  const isCustomerPortalDefault = mainMenuType === '1';
  const isSupplierPortalDefault = mainMenuType === '2';

  // Helper to check if a feature flag is enabled (only for boolean flags, not portal_mode)
  type BooleanFeatureFlags = Exclude<keyof FeatureFlags, 'portal_mode' | 'dashboard_tabs' | 'supplier_dashboard_tabs'>;
  // Effective delivery grain: per-customer value when the backend supplied
  // one, else the platform flag (pure schedule / pure PO-line tenants and
  // older backends behave exactly as before).
  const usesScheduleLines = effectivePoLineSchedules ?? (featureFlags.po_line_schedules ?? false);
  const deliveryGrain: DeliveryGrain = usesScheduleLines ? 'schedules' : 'po_lines';
  const isFeatureEnabled = (feature: BooleanFeatureFlags): boolean => {
    if (feature === 'po_line_schedules') return usesScheduleLines;
    return featureFlags[feature] ?? false; // Default to disabled if not set
  };

  // CR-6385: a line's customer must be one that opted in. A supplier serving an
  // enabled and a disabled customer gets the entry point only on the former.
  const isConcessionEnabledForCustomer = (customerId?: number | null): boolean =>
    customerId != null && concessionEnabledCustomerIds.includes(customerId);
  const isEscapeRootCauseEnabledForCustomer = (customerId?: number | null): boolean =>
    customerId != null && escapeRootCauseCustomerIds.includes(customerId);

  // Portal mode - determines navigation structure
  const portalMode: PortalMode = featureFlags.portal_mode || 'standard';
  // CR-5334: 'barratt' renamed to 'spend_analytics'; accept the legacy value
  // so a not-yet-migrated tenant still gets the right navigation.
  const isSpendAnalyticsMode = portalMode === 'spend_analytics' || portalMode === 'barratt';
  const isBarrattMode = isSpendAnalyticsMode;  // deprecated alias
  const isComplianceMode = portalMode === 'compliance';
  const isCustomerPortalMode = portalMode === 'customer_portal';

  // Helper to check if a dashboard tab is enabled (CR-4441).
  // Tabs added after CR-4441 default to OFF for existing tenants whose
  // PlatformSettings.dashboard_tabs JSON predates the new keys, so they
  // stay hidden until a tenant admin opts in via /admin → Dashboard Tabs.
  const NEW_TABS_DEFAULT_OFF = new Set(['scheduled', 'spot-orders']);
  const isDashboardTabEnabled = (tabId: string, isSupplier?: boolean): boolean => {
    const fallback = NEW_TABS_DEFAULT_OFF.has(tabId) ? false : !isSupplier;
    if (isSupplier) {
      return supplierDashboardTabs[tabId] ?? fallback;
    }
    return dashboardTabs[tabId] ?? fallback;
  };

  // Direct update for dashboard tab visibility (CR-4441)
  const updateDashboardTabs = useCallback((tabs: DashboardTabsVisibility) => {
    setDashboardTabs({ ...DEFAULT_DASHBOARD_TABS, ...tabs });
  }, []);

  const updateSupplierDashboardTabs = useCallback((tabs: DashboardTabsVisibility) => {
    setSupplierDashboardTabs({ ...DEFAULT_SUPPLIER_DASHBOARD_TABS, ...tabs });
  }, []);

  const isSupplierCardFieldEnabled = (fieldId: keyof SupplierCardFieldsVisibility): boolean => {
    return supplierCardFields[fieldId] ?? true;
  };

  const updateSupplierCardFields = useCallback((fields: SupplierCardFieldsVisibility) => {
    setSupplierCardFields({ ...DEFAULT_SUPPLIER_CARD_FIELDS, ...fields });
  }, []);

  const isDataImportTileEnabled = (tileId: keyof DataImportTilesVisibility): boolean => {
    return dataImportTiles[tileId] ?? true;
  };

  const updateDataImportTiles = useCallback((tiles: DataImportTilesVisibility) => {
    setDataImportTiles({ ...DEFAULT_DATA_IMPORT_TILES, ...tiles });
  }, []);

  const updateSupplierDefaultLandingPath = useCallback((path: string) => {
    setSupplierDefaultLandingPath(path || '/dashboard');
  }, []);

  const updateSupplierProfileLayout = useCallback((layout: SupplierProfileLayout) => {
    setSupplierProfileLayout(layout);
  }, []);

  const updateHelpLayout = useCallback((layout: HelpLayout) => {
    setHelpLayout(layout);
  }, []);

  // CR-5884: Open Orders hide-toggle helper. Returns true only when the
  // viewer is a supplier AND the admin toggle is ON. CR-8701: Communication is
  // hidden from every viewer, so hide_communication_box callers pass `true`.
  const isOpenOrdersControlHidden = useCallback(
    (key: keyof OpenOrdersVisibility, isSupplier: boolean): boolean => {
      if (!isSupplier) return false;
      return openOrdersVisibility[key] === true;
    },
    [openOrdersVisibility]
  );

  return (
    <MenuSettingsContext.Provider
      value={{
        menuVisibility,
        updateMenuVisibility,
        isMenuItemVisible,
        loadSettingsFromBackend,
        isLoading,
        mainMenuType,
        isCustomerPortalDefault,
        isSupplierPortalDefault,
        concessionsEnabled,
        concessionEnabledCustomerIds,
        isConcessionEnabledForCustomer,
        escapeRootCauseEnabled: escapeRootCauseCustomerIds.length > 0,
        isEscapeRootCauseEnabledForCustomer,
        featureFlags,
        isFeatureEnabled,
        deliveryGrain,
        usesScheduleLines,
        deliveryGrainCustomerId,
        portalMode,
        isSpendAnalyticsMode,
        isBarrattMode,
        isComplianceMode,
        isCustomerPortalMode,
        dashboardTabs,
        supplierDashboardTabs,
        isDashboardTabEnabled,
        updateDashboardTabs,
        updateSupplierDashboardTabs,
        supplierCardFields,
        isSupplierCardFieldEnabled,
        updateSupplierCardFields,
        dataImportTiles,
        isDataImportTileEnabled,
        updateDataImportTiles,
        supplierDefaultLandingPath,
        updateSupplierDefaultLandingPath,
        supplierProfileLayout,
        updateSupplierProfileLayout,
        helpLayout,
        updateHelpLayout,
        qualityMeasurePpk,
        enable8dScoring,
        writebackEnabled,
        warehouseModuleEnabled,
        deliveryAppealBulkEnabled,
        auditLiteEnabled,
        notificationIconTop,
        notificationIconSide,
        ncrFaultClassificationOptional,
        openOrdersVisibility,
        ncrCustomerReferenceLabel,
        isOpenOrdersControlHidden,
        purchasingTabs,
        qualityTabs,
      }}
    >
      {children}
    </MenuSettingsContext.Provider>
  );
};

/**
 * Accesses menu visibility settings, feature flags, and portal mode configuration.
 * Must be used within a {@link MenuSettingsProvider}.
 *
 * @returns Menu settings with visibility checks, feature flag helpers, and portal mode state
 */
export const useMenuSettings = (): MenuSettingsContextType => {
  const context = useContext(MenuSettingsContext);
  if (!context) {
    throw new Error('useMenuSettings must be used within a MenuSettingsProvider');
  }
  return context;
};
